import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Credential } from "../../types";
import { StatusChip } from "../status-chip";
import { formatDate } from "../../lib/format-date";

/**
 * The right-hand rail of a credential row: provenance chip, then expiry.
 *
 * Always the same two slots, whatever the expiry state. An earlier version
 * swapped between a plain date line, a badge, and a badge-plus-date depending
 * on the state, which made the column change shape from row to row and stopped
 * it scanning as a column at all. Expiring and expired now recolour the same
 * line rather than restructuring it.
 *
 * `tabular-nums` keeps the dates aligned down the page.
 */
export function CredentialStatusColumn({
  credential,
  appearance = "default",
}: {
  credential: Credential;
  appearance?: "default" | "panel";
}) {
  const expiresAt = credential.version?.expiresAt ?? null;
  const { expiryStatus } = credential;
  const satisfiedBy = !credential.present
    ? credential.requirementSatisfiedBy
    : null;

  return (
    <div
      className={cn(
        "flex shrink-0 flex-col items-end gap-1 pt-0.5",
        appearance === "panel" &&
          credential.present &&
          "flex-row flex-wrap items-center gap-x-2 sm:flex-col sm:items-end",
      )}
    >
      {satisfiedBy ? (
        <span className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-right text-xs font-medium text-slate-600">
          On file as {satisfiedBy.label}
        </span>
      ) : (
        <StatusChip
          status={credential.status}
          requirement={credential.requirement}
          label={
            credential.status === "source-supplied"
              ? credential.provenance?.label
              : null
          }
        />
      )}
      {expiresAt && expiryStatus === "expired" && (
        <span className="inline-flex items-center gap-1 text-xs font-medium tabular-nums text-red-600">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
          Expired {formatDate(expiresAt)}
        </span>
      )}
      {expiresAt && expiryStatus === "expiring" && (
        <span className="inline-flex items-center gap-1 text-xs font-medium tabular-nums text-amber-600">
          <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
          Expires {formatDate(expiresAt)}
        </span>
      )}
      {expiresAt && expiryStatus === "current" && (
        <span className="text-xs tabular-nums text-slate-500">
          Expires {formatDate(expiresAt)}
        </span>
      )}
    </div>
  );
}
